import { generateDiscountCode } from '../api/candyrack';
import { getCurrency } from './currency';
import { OfferUI } from '../types/ProductPagePlacement';
import { logInfo } from '../utils/logging';
import { PPPSelectedOffers } from './add-to-cart-ppp';
import { saveDiscountCodeForCandyrack } from '../utils/discounts';
import { formatMoney } from '../utils/formatMoney';
import applyDiscounts from '../../graphql/utils/applyDiscounts';

const applyDiscountCode = async ({
  offerDiscountCode,
  useRecommendedProducts,
  isSameProductOffer,
  offerId,
  offerProductId,
  offerProductVariantId,
  parentProductId,
  discountedPrice,
  discountedPriceFormatted,
  hasParent = true,
  storefrontAccessToken,
}: {
  offerDiscountCode: string | null;
  useRecommendedProducts: boolean;
  isSameProductOffer: boolean;
  offerId: number;
  offerProductId: number;
  offerProductVariantId: number | undefined;
  parentProductId: number;
  discountedPrice: number;
  discountedPriceFormatted: string;
  hasParent: boolean;
  storefrontAccessToken: string | undefined;
}) => {
  let discountCode = offerDiscountCode;

  // since recommended products or same product offers dont have discounts
  //  we need to generate discount code for them
  if (useRecommendedProducts || isSameProductOffer) {
    discountCode = await generateDiscountCode(
      window.Shopify.shop,
      offerId,
      offerProductId,
      1,
    );
    logInfo(
      `Generated discount code instead: ${discountCode} on Product Page Placement`,
    );
  }

  // apply discount since the discountCode is available now
  await saveDiscountCodeForCandyrack(
    discountCode as string,
    offerProductVariantId as number,
    discountedPrice,
    discountedPriceFormatted,
    parentProductId,
    hasParent,
    storefrontAccessToken,
  );

  return discountCode;
};

export const getDiscountedPrice = (
  originalPrice: number,
  offerPageSettings: OfferUI['offerPageSettings'],
) => {
  if (!offerPageSettings.applyDiscount) return null;

  if (offerPageSettings.discountAmountType === 'percentage') {
    const discountRaw =
      originalPrice * (offerPageSettings.discountAmount / 100);
    const discountTruncated = Math.floor(discountRaw * 100) / 100;
    const discountedProductPrice = originalPrice - discountTruncated;

    return discountedProductPrice;
  }

  if (offerPageSettings.discountAmountType === 'fixed_amount') {
    const discountedProductPrice =
      originalPrice - offerPageSettings.discountAmount * 100;
    return discountedProductPrice;
  }

  return null;
};

const getDiscountedPriceFormatted = (
  offerPageSettings: OfferUI['offerPageSettings'],
) => {
  if (offerPageSettings.discountAmountType === 'fixed_amount') {
    return formatMoney(
      Number(offerPageSettings.discountAmount) * 1,
      getCurrency().currency,
    );
  }

  return `${new Intl.NumberFormat(undefined, {
    maximumFractionDigits: 2,
  }).format(Number(offerPageSettings.discountAmount))}%`;
};

/*
 loop through the offers and each item that is selected and apply the discount if the offer has it active
 with the specific discount code.
*/
export const applyAllDiscountCodes = async (
  selectedOffers: PPPSelectedOffers,
  hasParent: boolean = true,
  storefrontAccessToken: string,
) => {
  const discountCodes: string[] = [];
  const selectedOffersKeys = Object.keys(selectedOffers);
  for (const selectedOfferKey of selectedOffersKeys) {
    const offer = selectedOffers[selectedOfferKey];
    if (offer.data.offerPageSettings.applyDiscount) {
      const applyDiscountPromises = offer.data.items
        .filter((offerItem) => offerItem.isSelected)
        .map(async (offerItem) => {
          const originalProductPrice =
            offerItem.product.variants?.find(
              (variant) => variant.id === offerItem.selectedVariantId,
            )?.price || offerItem.product.price;

          const discountedPrice = getDiscountedPrice(
            originalProductPrice as number,
            offer.data.offerPageSettings,
          ) as number;
          const discountedPriceFormatted = getDiscountedPriceFormatted(
            offer.data.offerPageSettings,
          );
          const discountCode = await applyDiscountCode({
            offerId: offer.data.id,
            offerDiscountCode: offer.data.offerPageSettings.discountCode,
            offerProductId: offerItem.itemId,
            offerProductVariantId: offerItem.selectedVariantId as number,
            useRecommendedProducts:
              offer.data.offerPageSettings.useRecommendedProducts,
            isSameProductOffer: offer.data.offerPageSettings.isSameProductOffer,
            parentProductId: Number(offerItem.block.product.id),
            discountedPrice,
            discountedPriceFormatted,
            hasParent,
            storefrontAccessToken: undefined, // passing undefined to avoid applying discounts via storefront api, we are applying them all at once below
          });
          if (discountCode) {
            discountCodes.push(discountCode);
          }
        });

      await Promise.all(applyDiscountPromises);
    }
  }

  if (discountCodes.length > 0) {
    await applyDiscounts({
      token: storefrontAccessToken,
      discountCodes: discountCodes,
    });
  }
};
